import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { vendors } from "@/mocks/products";
import FactoryAuthModal from "./FactoryAuthModal";
import BuyerAuthModal from "./BuyerAuthModal";
import FactoryProfileModal from "./FactoryProfileModal";

const CATEGORIES = ["All", "Golf Cart", "Clothes", "Sweater", "Pants", "Luggage", "Computer", "Machine", "Phone Case", "Backpack", "Restaurant Supplies", "Stove", "Hat", "Window", "Life Jacket", "Electronics", "Auto Parts", "Solar Panels", "Apparel", "Health & Beauty", "Home & Garden", "Chemicals"];

const PAGE_SIZE = 12;

const cinematicSlides = [
  {
    city: "New York",
    country: "USA",
    flag: "🇺🇸",
    label: "North America Hub",
    image: "https://public.readdy.ai/ai/img_res/5f219333a24a00ef2239a6f0e1d3f872.jpg",
  },
  {
    city: "Vancouver",
    country: "Canada",
    flag: "🇨🇦",
    label: "Pacific Gateway",
    image: "https://readdy.ai/api/search-image?query=bright%20sunny%20midday%20aerial%20view%20Vancouver%20Canada%20port%20large%20container%20ships%20snow%20capped%20mountains%20vivid%20blue%20ocean%20clear%20blue%20sky%20white%20clouds%20green%20trees%20waterfront%20city%20ultra%20bright%20cheerful%20daylight%20wide%20panoramic%20photography&width=1400&height=700&seq=bright_van_v2&orientation=landscape",
  },
  {
    city: "London",
    country: "UK",
    flag: "🇬🇧",
    label: "Europe Gateway",
    image: "https://readdy.ai/api/search-image?query=bright%20sunny%20midday%20aerial%20view%20London%20UK%20Tower%20Bridge%20Thames%20River%20sparkling%20water%20Canary%20Wharf%20glass%20towers%20clear%20blue%20sky%20white%20clouds%20vibrant%20colorful%20city%20wide%20panoramic%20ultra%20bright%20cheerful%20daylight%20photography&width=1400&height=700&seq=bright_lon_v2&orientation=landscape",
  },
  {
    city: "Paris",
    country: "France",
    flag: "🇫🇷",
    label: "European Hub",
    image: "https://readdy.ai/api/search-image?query=bright%20sunny%20midday%20aerial%20view%20Paris%20France%20Eiffel%20Tower%20gleaming%20Seine%20River%20sparkling%20blue%20sky%20white%20fluffy%20clouds%20Haussmann%20buildings%20warm%20golden%20light%20vibrant%20beautiful%20city%20wide%20panoramic%20ultra%20bright%20cheerful%20daylight%20photography&width=1400&height=700&seq=bright_par_v2&orientation=landscape",
  },
  {
    city: "Dubai",
    country: "UAE",
    flag: "🇦🇪",
    label: "Middle East Hub",
    image: "https://readdy.ai/api/search-image?query=bright%20sunny%20midday%20aerial%20view%20Dubai%20UAE%20Burj%20Khalifa%20gleaming%20glass%20skyscrapers%20Jebel%20Ali%20port%20container%20ships%20turquoise%20Persian%20Gulf%20vivid%20clear%20blue%20sky%20desert%20city%20ultra%20bright%20vibrant%20daylight%20wide%20panoramic%20photography&width=1400&height=700&seq=bright_dxb_v2&orientation=landscape",
  },
  {
    city: "Toronto",
    country: "Canada",
    flag: "🇨🇦",
    label: "Canada Hub",
    image: "https://readdy.ai/api/search-image?query=bright%20sunny%20midday%20aerial%20view%20Toronto%20Canada%20CN%20Tower%20gleaming%20Lake%20Ontario%20sparkling%20blue%20water%20waterfront%20district%20clear%20blue%20sky%20white%20clouds%20vibrant%20colorful%20city%20ultra%20bright%20cheerful%20daylight%20wide%20panoramic%20photography&width=1400&height=700&seq=bright_tor_v2&orientation=landscape",
  },
  {
    city: "Sydney",
    country: "Australia",
    flag: "🇦🇺",
    label: "Australia Hub",
    image: "https://readdy.ai/api/search-image?query=bright%20sunny%20midday%20aerial%20view%20Sydney%20Australia%20Opera%20House%20gleaming%20Harbour%20Bridge%20sparkling%20blue%20harbour%20cargo%20ships%20clear%20blue%20sky%20white%20clouds%20vibrant%20colorful%20city%20ultra%20bright%20cheerful%20daylight%20wide%20panoramic%20photography&width=1400&height=700&seq=bright_syd_v2&orientation=landscape",
  },
  {
    city: "Miami / Florida",
    country: "USA",
    flag: "🇺🇸",
    label: "Southeast USA Hub",
    image: "https://readdy.ai/api/search-image?query=bright%20sunny%20midday%20aerial%20view%20Miami%20Florida%20port%20cargo%20ships%20turquoise%20crystal%20clear%20ocean%20water%20gleaming%20white%20buildings%20clear%20blue%20sky%20white%20clouds%20vibrant%20tropical%20colorful%20city%20ultra%20bright%20cheerful%20daylight%20wide%20panoramic%20photography&width=1400&height=700&seq=bright_mia_v2&orientation=landscape",
  },
];

export default function TopVendors() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState("All");
  const [page, setPage] = useState(1);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [showFactoryAuth, setShowFactoryAuth] = useState(false);
  const [showBuyerAuth, setShowBuyerAuth] = useState(false);
  const [loggedInFactory, setLoggedInFactory] = useState<string | null>(null);
  const [loggedInBuyer, setLoggedInBuyer] = useState<string | null>(null);
  const [showFactoryProfile, setShowFactoryProfile] = useState(false);
  const [authSuccess, setAuthSuccess] = useState<string | null>(null);

  const filtered = activeCategory === "All"
    ? vendors
    : vendors.filter((v) => v.category === activeCategory);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const displayed = filtered.slice(0, page * PAGE_SIZE);

  const goToSlide = (idx: number) => {
    if (transitioning) return;
    setTransitioning(true);
    setTimeout(() => {
      setCurrentSlide(idx);
      setTransitioning(false);
    }, 400);
  };

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setTransitioning(true);
      setTimeout(() => {
        setCurrentSlide(prev => (prev + 1) % cinematicSlides.length);
        setTransitioning(false);
      }, 600);
    }, 4000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const handleCategoryChange = (cat: string) => {
    setActiveCategory(cat);
    setPage(1);
  };

  const handleFactorySuccess = (name: string) => {
    setLoggedInFactory(name);
    setShowFactoryAuth(false);
    setAuthSuccess(`Welcome back, ${name}! Your factory dashboard is ready.`);
    setTimeout(() => setAuthSuccess(null), 4000);
  };

  const handleBuyerSuccess = (name: string) => {
    setLoggedInBuyer(name);
    setShowBuyerAuth(false);
    setAuthSuccess(`Welcome, ${name}! You can now post requirements for free.`);
    setTimeout(() => setAuthSuccess(null), 4000);
  };

  const slide = cinematicSlides[currentSlide];

  return (
    <>
      {showFactoryAuth && (
        <FactoryAuthModal
          onClose={() => setShowFactoryAuth(false)}
          onSuccess={handleFactorySuccess}
        />
      )}
      {showBuyerAuth && (
        <BuyerAuthModal
          onClose={() => setShowBuyerAuth(false)}
          onSuccess={handleBuyerSuccess}
        />
      )}
      {showFactoryProfile && loggedInFactory && (
        <FactoryProfileModal
          factoryName={loggedInFactory}
          onClose={() => setShowFactoryProfile(false)}
          onDonate={() => { setShowFactoryProfile(false); navigate("/community"); }}
        />
      )}

      {/* ── Cinematic Background Section ── */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: "520px" }}>
        {/* Cinematic background image */}
        <div className="absolute inset-0">
          <img
            key={currentSlide}
            src={slide.image}
            alt={slide.city}
            className={`w-full h-full object-cover object-center transition-opacity duration-700 ${transitioning ? "opacity-0" : "opacity-100"}`}
            style={{ transform: "scale(1.04)", animation: "kenBurns 8s ease-in-out infinite alternate" }}
          />
          {/* Dark overlay for readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
          {/* Side vignette */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black/30"></div>
        </div>

        {/* Ken Burns keyframe */}
        <style>{`
          @keyframes kenBurns {
            0% { transform: scale(1.0) translateX(0px); }
            100% { transform: scale(1.08) translateX(-20px); }
          }
        `}</style>

        {/* Content overlay */}
        <div className="relative z-10 max-w-screen-2xl mx-auto px-4 md:px-8 py-16">
          {/* Section header */}
          <div className="text-center mb-10">
            <p className="text-amber-400 text-sm font-semibold uppercase tracking-widest mb-2">Global Trade Platform</p>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white leading-tight mb-3">
              Crown Global Factory &amp; Buyer<br className="hidden md:block" />
              <span className="text-amber-400"> Best Platform</span>
            </h2>
            <p className="text-white/70 text-sm max-w-2xl mx-auto">
              Connecting verified factories with buyers across New York, Vancouver, London, Paris, Dubai, Toronto, Sydney &amp; beyond
            </p>
          </div>

          {/* City location ticker */}
          <div className="flex items-center justify-center gap-3 mb-8 flex-wrap">
            {cinematicSlides.map((s, i) => (
              <button
                key={i}
                onClick={() => goToSlide(i)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  currentSlide === i
                    ? "bg-amber-400 text-white scale-105"
                    : "bg-white/15 backdrop-blur-sm text-white/80 hover:bg-white/25"
                }`}
              >
                <span>{s.flag}</span>
                <span>{s.city}</span>
              </button>
            ))}
          </div>

          {/* Current city display */}
          <div className={`flex items-center justify-center gap-3 mb-10 transition-all duration-500 ${transitioning ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"}`}>
            <div className="flex items-center gap-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-6 py-3">
              <span className="text-3xl">{slide.flag}</span>
              <div>
                <p className="text-white font-extrabold text-xl leading-tight">{slide.city}</p>
                <p className="text-amber-300 text-xs font-semibold">{slide.label} · {slide.country}</p>
              </div>
              <div className="w-px h-10 bg-white/20 mx-2"></div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-white/80 text-xs font-semibold">Warehouse Active</span>
              </div>
            </div>
          </div>

          {/* Auth CTA cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 max-w-3xl mx-auto">
            {/* Factory CTA */}
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-5 flex items-center gap-4 hover:bg-white/15 transition-colors">
              <div className="w-12 h-12 flex items-center justify-center bg-amber-400/30 border border-amber-400/50 rounded-xl shrink-0">
                <i className="ri-building-2-line text-amber-300 text-2xl"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-extrabold text-sm">Are You a Factory?</p>
                <p className="text-white/60 text-xs mt-0.5">Sign up or login with email or phone. Edit products, FOB price, connect with buyers.</p>
              </div>
              {loggedInFactory ? (
                <button
                  onClick={() => setShowFactoryProfile(true)}
                  className="bg-amber-400 hover:bg-amber-500 text-white font-bold text-xs px-4 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap shrink-0"
                >
                  <i className="ri-dashboard-line mr-1"></i>Dashboard
                </button>
              ) : (
                <button
                  onClick={() => setShowFactoryAuth(true)}
                  className="bg-amber-400 hover:bg-amber-500 text-white font-bold text-xs px-4 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap shrink-0"
                >
                  <i className="ri-login-box-line mr-1"></i>Join / Login
                </button>
              )}
            </div>

            {/* Buyer CTA */}
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-5 flex items-center gap-4 hover:bg-white/15 transition-colors">
              <div className="w-12 h-12 flex items-center justify-center bg-emerald-400/30 border border-emerald-400/50 rounded-xl shrink-0">
                <i className="ri-user-star-line text-emerald-300 text-2xl"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-extrabold text-sm">Are You a Buyer?</p>
                <p className="text-white/60 text-xs mt-0.5">Sign up free with email or phone. Post requirements, get quotes from 20 million+ factories.</p>
              </div>
              {loggedInBuyer ? (
                <button
                  onClick={() => navigate("/buyer-post")}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-xs px-4 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap shrink-0"
                >
                  <i className="ri-file-list-3-line mr-1"></i>Post RFQ
                </button>
              ) : (
                <button
                  onClick={() => setShowBuyerAuth(true)}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-xs px-4 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap shrink-0"
                >
                  <i className="ri-user-add-line mr-1"></i>Join Free
                </button>
              )}
            </div>
          </div>

          {/* Stats strip */}
          <div className="flex flex-wrap justify-center gap-4">
            {[
              { icon: "ri-building-2-line", value: "20M+", label: "Verified Factories" },
              { icon: "ri-global-line", value: "190+", label: "Countries" },
              { icon: "ri-store-3-line", value: "28", label: "Overseas Warehouses" },
              { icon: "ri-ship-line", value: "$120B+", label: "Annual Trade" },
            ].map(s => (
              <div key={s.label} className="flex items-center gap-2.5 bg-white/10 backdrop-blur-sm border border-white/15 rounded-xl px-4 py-2.5">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-400/20 rounded-lg">
                  <i className={`${s.icon} text-amber-300 text-base`}></i>
                </div>
                <div>
                  <p className="text-white font-extrabold text-sm leading-tight">{s.value}</p>
                  <p className="text-white/60 text-xs">{s.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Slide dots */}
          <div className="flex justify-center gap-2 mt-8">
            {cinematicSlides.map((_, i) => (
              <button
                key={i}
                onClick={() => goToSlide(i)}
                className={`transition-all duration-300 rounded-full cursor-pointer ${currentSlide === i ? "w-6 h-2 bg-amber-400" : "w-2 h-2 bg-white/40 hover:bg-white/60"}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── Factory Grid Section ── */}
      <section className="py-14 bg-white">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">

          {/* Success toast */}
          {authSuccess && (
            <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl px-5 py-3 mb-5 flex items-center gap-3 text-sm">
              <i className="ri-checkbox-circle-fill text-lg"></i>
              <span className="font-semibold">{authSuccess}</span>
            </div>
          )}

          {/* Donation nudge for big sellers */}
          {loggedInFactory && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl px-5 py-4 mb-6 flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-rose-100 rounded-xl shrink-0">
                <i className="ri-heart-fill text-rose-500 text-xl"></i>
              </div>
              <div className="flex-1">
                <p className="font-bold text-gray-800 text-sm">Selling Many Containers? Share the Love!</p>
                <p className="text-gray-500 text-xs mt-0.5">If your factory is doing well, consider donating to help infants who need milk, clothes, and love worldwide.</p>
              </div>
              <button
                onClick={() => navigate("/community")}
                className="bg-rose-500 hover:bg-rose-600 text-white font-bold text-xs px-4 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap shrink-0"
              >
                <i className="ri-heart-fill mr-1"></i>Donate Now
              </button>
            </div>
          )}

          {/* Sub-header */}
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-6 gap-4">
            <div>
              <p className="text-amber-500 text-sm font-semibold uppercase tracking-widest mb-1">Trusted Partners</p>
              <h3 className="text-2xl md:text-3xl font-extrabold text-gray-900">
                20 Million+ Factories &amp; 600 Million+ Buyers from 30+ Countries
              </h3>
            </div>
            <button
              onClick={() => navigate("/marketplace")}
              className="hidden md:flex items-center gap-1.5 text-amber-500 hover:text-amber-600 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              View All Suppliers <i className="ri-arrow-right-line"></i>
            </button>
          </div>

          {/* Category Filter */}
          <div className="flex gap-2 flex-wrap mb-8">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => handleCategoryChange(cat)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? "bg-amber-400 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-amber-50 hover:text-amber-600"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {displayed.map((v) => (
              <div
                key={v.id}
                onClick={() => loggedInFactory ? setShowFactoryProfile(true) : navigate("/marketplace")}
                className="bg-white border border-gray-100 rounded-xl overflow-hidden cursor-pointer group hover:-translate-y-1 transition-transform duration-300"
              >
                <div className="w-full h-36 overflow-hidden relative">
                  <img
                    src={v.image}
                    alt={v.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <span className="absolute top-2 left-2 bg-amber-400 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                    {v.category}
                  </span>
                  {v.id > 34 && (
                    <span className="absolute top-2 right-2 bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                      New
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-bold text-gray-800 text-sm leading-snug">{v.name}</h3>
                    {v.verified && (
                      <span className="shrink-0 ml-2 w-5 h-5 flex items-center justify-center bg-green-100 rounded-full">
                        <i className="ri-shield-check-fill text-green-500 text-xs"></i>
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-3">
                    <span>{v.flag}</span>
                    <span>{v.country}</span>
                    <span className="text-gray-300">·</span>
                    <span>{v.years} yrs</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">{v.products.toLocaleString()} products</span>
                    <div className="flex items-center gap-1">
                      <i className="ri-star-fill text-amber-400"></i>
                      <span className="font-semibold text-gray-700">{v.rating}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Load More */}
          {page < totalPages && (
            <div className="flex justify-center mt-10">
              <button
                onClick={() => setPage((p) => p + 1)}
                className="flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-white font-semibold px-8 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                Load More Factories <i className="ri-arrow-down-line"></i>
              </button>
            </div>
          )}

          {/* Mobile all suppliers link */}
          <div className="flex justify-center mt-6 md:hidden">
            <button
              onClick={() => navigate("/marketplace")}
              className="flex items-center gap-1.5 text-amber-500 hover:text-amber-600 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              View All Suppliers <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
